import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { emptySankeyFormValues } from 'utils/form';
import { useDiagramLoader, useDiagramSaver } from 'utils/syncing';

import { SankeyFormAdditionalContext } from '../../contexts/sankeyFormContext';
import { canUserEditDiagram } from '../../utils/team';
import { useCurrentUser } from '../../utils/user';
import { sankeyObjectSchema } from '../../utils/validation';

type Props = {
  children: React.ReactNode;
};

/*
 * This component provides the Sankey form context and handles loading and saving
 * the Sankey diagram data. It creates a form instance and uses `useDiagramLoader`
 * and `useDiagramSaver` hooks to manage loading and saving the Sankey diagram data.
 * */
const SankeyFormProvider: React.FC<Props> = ({ children }) => {
  const form = useNewSankeyForm();
  const { user } = useCurrentUser();

  const saveMutation = useDiagramSaver(form);
  const { data: sankey, ...loadQuery } = useDiagramLoader(form);

  const canUpdate = canUserEditDiagram(sankey ?? undefined, user);

  return (
    <FormProvider {...form}>
      <SankeyFormAdditionalContext.Provider
        value={{
          canUpdate,
          isErrorFetching: loadQuery.isError,
          isFetching: loadQuery.isFetching,
          isLoading: loadQuery.isLoading,
          // For authenticated users we show the diagram as read-only if and only
          // if they can't update it, but we let anonymous users edit example, template
          // and official diagrams (although they can't update them directly).
          isReadOnly: user?.is_authenticated
            ? !canUpdate
            : !(
                sankey?.is_main_example ||
                sankey?.is_template ||
                sankey?.is_official
              ),
          isSaving: saveMutation.isLoading,
          lastSaveTimestamp: saveMutation.lastSaveTimestamp,
          lastSaveError: saveMutation.lastSaveError,
        }}
      >
        {children}
      </SankeyFormAdditionalContext.Provider>
    </FormProvider>
  );
};

export const useNewSankeyForm = (defaultValues = emptySankeyFormValues) =>
  useForm<SankeyFormValues>({
    mode: 'onChange',
    resolver: yupResolver(sankeyObjectSchema),
    defaultValues,
  });

export default SankeyFormProvider;
